import React, { useState, useEffect } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { SettingsService } from "./services/SettingsService";
import { SettingsCard } from "./components/SettingsCard";
import { ToggleSwitch } from "./components/ToggleSwitch";
import { TextInput, SelectInput, PrimaryButton, SecondaryButton } from "./components/FormElements";
import {
  Settings as SettingsIcon,
  Users,
  Route,
  Bell,
  Shield,
  Database,
  Code,
  Truck,
  CheckCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "./SettingsPage.css";

export function SettingsPage() {
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    general: {
      platformName: "Smart Cargo",
      defaultLanguage: "en",
      timeZone: "UTC+4 (Baku)",
      dateTimeFormat: "DD/MM/YYYY HH:mm",
    },
    user: {
      allowCourierRegistration: true,
      defaultCourierStatus: "pending",
    },
    delivery: {
      defaultPriority: "normal",
      trafficRouting: true,
      etaMode: "dynamic",
      maxRadius: "50",
    },
    notification: {
      systemNotifications: true,
      emailAlerts: true,
      paymentNotifications: true,
      deliveryAlerts: true,
    },
    security: {
      minPasswordLength: "8",
      sessionTimeout: "30",
      specialCharsRequired: true,
      twoFactorEnabled: false,
    },
    data: {
      retentionPeriod: "12",
      backupFrequency: "daily",
    },
    api: {
      openStreetMapConnected: true,
      trafficAPIEnabled: true,
      apiKey: "sk_live_••••••••••••••••",
    },
    fleet: {
      vehicleTracking: true,
      maintenanceAlerts: true,
    },
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Load initial settings
    SettingsService.getSettings().then((data) => {
      setFormData(data);
      setIsLoading(false);
    });
  }, []);

  const handleChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await SettingsService.updateSettings(formData);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save settings", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="settings-layout"><p>Loading settings...</p></div>;
  }

  return (
    <div className="settings-layout">
      <div>
        <h1 className="settings-header__title">{t.settingsTitle}</h1>
        <p className="settings-header__subtitle">{t.settingsSubtitle}</p>
      </div>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="settings-success"
          >
            <CheckCircle className="w-5 h-5" style={{ color: "#059669" }} />
            <span className="settings-success__text">{t.settingsUpdated}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="settings-content">
        {/* General Settings */}
        <SettingsCard icon={SettingsIcon} title={t.generalSettings} delay={0}>
          <div className="settings-grid">
            <TextInput label={t.platformName} value={formData.general.platformName} onChange={(val) => handleChange("general", "platformName", val)} />

            <SelectInput label={t.defaultLanguage} value={formData.general.defaultLanguage} onChange={(val) => handleChange("general", "defaultLanguage", val)} options={[{value: "en", label: "English (ENG)"}, {value: "aze", label: "Azərbaycan (AZE)"}, {value: "ru", label: "Русский (RU)"}]} />

            <SelectInput label={t.timeZone} value={formData.general.timeZone} onChange={(val) => handleChange("general", "timeZone", val)} options={["UTC+4 (Baku)", "UTC+0 (UTC)", "UTC+3 (Moscow)"]} />

            <SelectInput label={t.dateTimeFormat} value={formData.general.dateTimeFormat} onChange={(val) => handleChange("general", "dateTimeFormat", val)} options={["DD/MM/YYYY HH:mm", "MM/DD/YYYY HH:mm", "YYYY-MM-DD HH:mm"]} />
          </div>
        </SettingsCard>

        {/* User Management */}
        <SettingsCard icon={Users} title={t.userManagement} delay={0.1}>
          <div className="switch-row switch-row--highlight">
            <div className="switch-row__content">
              <p className="switch-row__title">{t.allowCourierRegistration}</p>
              <p className="switch-row__desc">Allow new couriers to register</p>
            </div>
            <ToggleSwitch
              checked={formData.user.allowCourierRegistration}
              onChange={(checked) => handleChange("user", "allowCourierRegistration", checked)}
            />
          </div>

          <SelectInput label={t.defaultCourierStatus} value={formData.user.defaultCourierStatus} onChange={(val) => handleChange("user", "defaultCourierStatus", val)} options={[{value: "active", label: "Active"}, {value: "pending", label: "Pending"}]} />

          <div><SecondaryButton>{t.addNewAdmin}</SecondaryButton></div>
        </SettingsCard>

        {/* Delivery & Routing */}
        <SettingsCard icon={Route} title={t.deliveryRouting} delay={0.2}>
          <div className="form-group">
            <label className="form-label">{t.defaultDeliveryPriority}</label>
            <div className="segmented-control">
              <button
                onClick={() => handleChange("delivery", "defaultPriority", "normal")}
                className={`segmented-btn ${formData.delivery.defaultPriority === "normal" ? "segmented-btn--active" : "segmented-btn--inactive"}`}
              >
                {t.normal}
              </button>
              <button
                onClick={() => handleChange("delivery", "defaultPriority", "urgent")}
                className={`segmented-btn ${formData.delivery.defaultPriority === "urgent" ? "segmented-btn--active" : "segmented-btn--inactive"}`}
              >
                {t.urgent}
              </button>
            </div>
          </div>

          <div className="switch-row switch-row--border">
            <div className="switch-row__content">
              <p className="switch-row__title">{t.enableTrafficRouting}</p>
              <p className="switch-row__desc">Use real-time traffic data for routing</p>
            </div>
            <ToggleSwitch
              checked={formData.delivery.trafficRouting}
              onChange={(checked) => handleChange("delivery", "trafficRouting", checked)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t.etaCalculationMode}</label>
            <div className="segmented-control">
              <button
                onClick={() => handleChange("delivery", "etaMode", "static")}
                className={`segmented-btn ${formData.delivery.etaMode === "static" ? "segmented-btn--active" : "segmented-btn--inactive"}`}
              >
                {t.static}
              </button>
              <button
                onClick={() => handleChange("delivery", "etaMode", "dynamic")}
                className={`segmented-btn ${formData.delivery.etaMode === "dynamic" ? "segmented-btn--active" : "segmented-btn--inactive"}`}
              >
                {t.dynamic}
              </button>
            </div>
          </div>

          <TextInput type="number" label={`${t.maxDeliveryRadius} (km)`} value={formData.delivery.maxRadius} onChange={(val) => handleChange("delivery", "maxRadius", val)} />
        </SettingsCard>

        {/* Notification Settings */}
        <SettingsCard icon={Bell} title={t.notificationSettingsSystem} delay={0.3}>
          <div className="switch-row switch-row--subtle">
            <span className="switch-row__title">{t.enableSystemNotifications}</span>
            <ToggleSwitch
              checked={formData.notification.systemNotifications}
              onChange={(checked) => handleChange("notification", "systemNotifications", checked)}
            />
          </div>

          <div className="switch-row switch-row--subtle">
            <span className="switch-row__title">{t.enableEmailAlerts}</span>
            <ToggleSwitch
              checked={formData.notification.emailAlerts}
              onChange={(checked) => handleChange("notification", "emailAlerts", checked)}
            />
          </div>

          <div className="switch-row switch-row--subtle">
            <span className="switch-row__title">{t.paymentNotifications}</span>
            <ToggleSwitch
              checked={formData.notification.paymentNotifications}
              onChange={(checked) => handleChange("notification", "paymentNotifications", checked)}
            />
          </div>

          <div className="switch-row switch-row--subtle">
            <span className="switch-row__title">{t.deliveryStatusAlerts}</span>
            <ToggleSwitch
              checked={formData.notification.deliveryAlerts}
              onChange={(checked) => handleChange("notification", "deliveryAlerts", checked)}
            />
          </div>
        </SettingsCard>

        {/* Security Settings */}
        <SettingsCard icon={Shield} title={t.securitySettingsSystem} delay={0.4}>
          <div className="settings-grid">
            <TextInput type="number" label={t.minimumLength} value={formData.security.minPasswordLength} onChange={(val) => handleChange("security", "minPasswordLength", val)} />

            <TextInput type="number" label={`${t.sessionTimeout} (minutes)`} value={formData.security.sessionTimeout} onChange={(val) => handleChange("security", "sessionTimeout", val)} />
          </div>

          <div className="switch-row switch-row--border">
            <span className="switch-row__title">{t.specialCharsRequired}</span>
            <ToggleSwitch
              checked={formData.security.specialCharsRequired}
              onChange={(checked) => handleChange("security", "specialCharsRequired", checked)}
            />
          </div>

          <div className="switch-row switch-row--border">
            <span className="switch-row__title">{t.enable2FA}</span>
            <ToggleSwitch
              checked={formData.security.twoFactorEnabled}
              onChange={(checked) => handleChange("security", "twoFactorEnabled", checked)}
            />
          </div>
        </SettingsCard>

        {/* Data & Storage Settings */}
        <SettingsCard icon={Database} title={t.dataStorage} delay={0.5}>
          <div className="settings-grid">
            <SelectInput label={t.dataRetentionPeriod} value={formData.data.retentionPeriod} onChange={(val) => handleChange("data", "retentionPeriod", val)} options={[{value: "6", label: "6 months"}, {value: "12", label: "12 months"}, {value: "24", label: "24 months"}]} />

            <SelectInput label={t.backupFrequency} value={formData.data.backupFrequency} onChange={(val) => handleChange("data", "backupFrequency", val)} options={[{value: "daily", label: "Daily"}, {value: "weekly", label: "Weekly"}, {value: "monthly", label: "Monthly"}]} />
          </div>

          <div><SecondaryButton>{t.exportData}</SecondaryButton></div>
        </SettingsCard>

        {/* API & Integration */}
        <SettingsCard icon={Code} title={t.apiIntegration} delay={0.6}>
          <div className="status-row">
            <div>
              <p className="status-row__title">{t.openStreetMapAPI}</p>
              <p className="status-row__subtitle">{t.connected}</p>
            </div>
            <div className="status-indicator"></div>
          </div>

          <div className="switch-row switch-row--border">
            <span className="switch-row__title">{t.trafficAPI}</span>
            <ToggleSwitch
              checked={formData.api.trafficAPIEnabled}
              onChange={(checked) => handleChange("api", "trafficAPIEnabled", checked)}
            />
          </div>

          <div className="form-group"><label className="form-label">{t.apiKey}</label><div className="input-with-button"><input type="password" value={formData.api.apiKey} onChange={(e) => handleChange("api", "apiKey", e.target.value)} className="form-input" /><SecondaryButton>{t.testConnection}</SecondaryButton></div></div>
        </SettingsCard>

        {/* Vehicle & Fleet */}
        <SettingsCard icon={Truck} title={t.vehicleFleet} delay={0.7}>
          <div className="switch-row switch-row--highlight">
            <div className="switch-row__content">
              <p className="switch-row__title">{t.enableVehicleTracking}</p>
              <p className="switch-row__desc">Real-time GPS tracking for all vehicles</p>
            </div>
            <ToggleSwitch
              checked={formData.fleet.vehicleTracking}
              onChange={(checked) => handleChange("fleet", "vehicleTracking", checked)}
            />
          </div>

          <div className="switch-row switch-row--border">
            <span className="switch-row__title">{t.maintenanceAlerts}</span>
            <ToggleSwitch
              checked={formData.fleet.maintenanceAlerts}
              onChange={(checked) => handleChange("fleet", "maintenanceAlerts", checked)}
            />
          </div>
        </SettingsCard>

        <div className="settings-actions">
          <PrimaryButton onClick={handleSave} disabled={isSubmitting}>{isSubmitting ? "Saving..." : t.saveChanges}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}
export default SettingsPage;
